<template>
  <Preloader v-if="localLoading" />
  <VRow v-else class="h-100">
    <VCol cols="12" class="pt-0 pt-md-2 d-flex flex-column h-100">
      <VForm
        lazy-validation
        class="d-flex flex-column h-100"
        @submit.prevent="publish"
        @keydown.enter.prevent
      >
        <VCol class="flex-grow-1 h-100 pb-0">
          <VRow class="vacancy-step-testing__choice-type justify-space-between">
            <div class="vacancy-step-testing__title">
              {{ $t('testing') }}
            </div>
            <VRadioGroup
              v-model="testType"
              @change="changeTestType()"
              :disabled="testLoading"
              class="vacancy-step-testing__choice-score-type_radio-group"
            >
              <div class="mb-3">
                <VRadio :label="$t('notRequired')" :value="testTypes.NOT_REQUIRED" />
              </div>
              <div class="mb-3">
                <VRadio :label="$t('cutoff')" :value="testTypes.CUTOFF" />
                <div v-if="testType === testTypes.CUTOFF">
                  <div class="vacancy-step-testing__radio-label">
                    {{ $t('explanationTestTextSubtitle') }}
                  </div>
                  <div class="vacancy-step-testing__radio-input-block">
                    <div class="vacancy-step-testing__radio-input-block_points">
                      <VTextField
                        v-model="minimalScore"
                        :disabled="testLoading"
                        outlined
                        dense
                        type="number"
                        required
                        class="vacancy-step-testing__radio-input"
                        placeholder="0"
                        min="0"
                        max="100"
                        oninput="validity.valid||(value='');"
                        :error-messages="minimalScoreErrorMessages"
                        @keydown.enter.prevent
                        @input="updateTest({ debounce: true })"
                      />
                      <span>%</span>
                    </div>
                    <div class="vacancy-step-testing__radio-input-block_label">
                      ( <span>{{ $t('numberOfPoints') }} : </span> {{ minimalScoreWithPercent }} )
                    </div>
                  </div>
                </div>
                <div
                  v-if="testType === testTypes.CUTOFF && minimalScore == 100"
                  class="vacancy-step-testing__radio-warning"
                >
                  {{ $t('explanationTestTextWarning') }}
                </div>
                <div
                  v-if="testType === testTypes.CUTOFF && minimalScoreWithPercent == 0"
                  class="vacancy-step-testing__radio-warning"
                >
                  {{ $t('explanationTestTextWarning0') }}
                </div>
                <div class="vacancy-step-testing__radio-label">
                  {{ $t('explanationTestTextLabel') }}
                </div>
              </div>
              <div>
                <VRadio :label="$t('clarifying')" :value="testTypes.EXPLANATION" />
                <div class="vacancy-step-testing__radio-label">
                  {{ $t('cutoffTestTextLabel') }}
                </div>
              </div>
            </VRadioGroup>
          </VRow>
          <VRow
            v-if="vacancyTestWithType"
            class="vacancy-step-testing__choice-type justify-space-between mt-7"
          >
            <div class="vacancy-step-testing__points-info_title">
              {{ $t('timeToComplete') }}
            </div>
            <VRadioGroup
              v-model="testTimerRadio"
              :disabled="testLoading"
              class="vacancy-step-testing__points-info_radio-group"
            >
              <VRadio
                :label="$t('withoutLimit')"
                :value="false"
                @change="updateTest()"
                class="mb-3"
              />
              <div>
                <VRadio :label="$t('withLimit')" :value="true" class="mb-3" />
                <div v-if="testTimerRadio" class="vacancy-step-testing__points-info_inputs">
                  <div>
                    <input
                      v-model="hours"
                      :disabled="testLoading"
                      class="vacancy-step-testing__points-info_time"
                      type="number"
                      placeholder="0"
                      min="0"
                      oninput="validity.valid||(value='');"
                      @keydown.enter.prevent
                      @input="updateTest({ debounce: true })"
                    />{{ $t('hours') }}
                    <input
                      v-model="minutes"
                      :disabled="testLoading"
                      class="vacancy-step-testing__points-info_time"
                      type="number"
                      placeholder="0"
                      min="0"
                      oninput="validity.valid||(value='');"
                      @keydown.enter.prevent
                      @input="updateTest({ debounce: true })"
                    />{{ $t('minutes') }}
                  </div>
                </div>
              </div>
            </VRadioGroup>
          </VRow>
        </VCol>
        <template v-if="vacancyTestWithType && testInfo">
          <VCol class="questions-block">
            <VRow class="flex-column">
              <div
                v-for="q in questions"
                :key="getQuestionKey(q)"
                :style="{ order: q.order }"
                class="vacancy-step-testing__q"
              >
                <TestQuestion
                  :key="q.key"
                  :idx="q.order"
                  :question="q"
                  :show-validation-messages-global="showValidationMessagesGlobal"
                  :last-question-order="lastQuestionOrder"
                  @deleteQuestion="deleteQuestion"
                  @saveQuestion="saveQuestion"
                  @deleteImage="deleteQuestionImage"
                  @changeQuestionOrderUp="changeQuestionOrderUp"
                  @changeQuestionOrderDown="changeQuestionOrderDown"
                />
              </div>
            </VRow>
            <div class="add-question-block">
              <VBtn color="success" @click="addQuestion()">
                {{ $t('addQuestion') }}
              </VBtn>
            </div>
          </VCol>
        </template>
      </VForm>

      <div class="vacancy-step-testing__buttons-controls">
        <VBtn
          v-if="vacancyActiveWithPublishedTest"
          dark
          color="red"
          elevation="0"
          :loading="loading"
          @click="unpublishTest"
        >
          {{ $t('unpublish') }}
        </VBtn>
        <VBtn 
          color="success" 
          elevation="0" 
          :loading="loading" 
          :disabled="loading"
          @click="publish"
        >
          {{ successBtnText }}
          <span class="ml-1" v-if="finishButtonText">&rarr;</span>
        </VBtn>
        <div
          v-if="vacancyActiveWithNotRequiredTest"
          class="vacancy-step-testing__buttons-controls_changes-text"
        >
          <template v-if="areTestsEqual && !vacancyDraft">
            <img :src="require('@/assets/img/test/check.svg')" alt="trash" />
          </template>
          <template v-else-if="!areTestsEqual && !vacancyDraft">
            <img :src="require('@/assets/img/test/info.svg')" alt="trash" />
          </template>
          <span :class="{ 'black-text': !areTestsEqual }" class="green-text">
            {{ areTestsEqualText }}
          </span>
        </div>
      </div>
    </VCol>
  </VRow>
</template>

<script>
import Preloader from '@/components/Preloader.vue';
import TestQuestion from '@/components/Test/TestQuestion.vue';
import ValidationMixin from '@/mixins/ValidationMixin';
import {
  computed,
  nextTick,
  onMounted,
  ref,
  toRefs,
  watch,
  onBeforeUnmount,
} from '@vue/composition-api';
import useTest from '@/compositions/useTest';
import useQuestion from '@/compositions/useQuestion';
import useVacancies from '@/compositions/useVacancies';
import { useI18n } from 'vue-i18n-composable';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import {
  testEstimationTypes, testQuestionTypes, testTypes, vacancyStatus,
} from '@/enums';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { objectToFormData } from '@/functions';
import Question from '@/orm/models/Test/Question';

export default {
  name: 'TestForm',
  components: {
    Preloader,
    TestQuestion,
  },
  mixins: [ValidationMixin],
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
    finishButtonText: {
      type: String,
      required: false,
      default: null,
    },
  },
  setup(props, { emit, root }) {
    const { vacancyId, finishButtonText, disableBackButton } = toRefs(props);
    const localLoading = ref(true);
    const {
      createTest,
      updateTest: updateBaseTest,
      publishTest: publishBaseTest,
      unpublishTest: unpublishBaseTest,
      removeTest: removeBaseTest,
      getTestInfo,
      getRedactingTestInfo,
      createRedactingTestInfo,
      getTestInfoByVacancy,
      deleteAll: deleteAllTests,
    } = useTest();

    const { t } = useI18n();

    const vacancy = computed(() => Vacancy.query().find(vacancyId.value));

    const baseTestInfo = ref(getTestInfoByVacancy(vacancyId.value));

    const publishedTestInfo = ref(null);
    const redactingTestInfo = ref(null);

    function removeTestKeysForEqual(testInfo) {
      const keysToRemove = ['id', '$id'];
      const keysQuestionToRemove = ['id', '$id', 'testId'];
      const keyAnswerToRemove = ['id', 'testQuestionId'];

      keysToRemove.forEach((key) => {
        delete testInfo[key];
      });

      for (const q of testInfo.questions) {
        keysQuestionToRemove.forEach((key) => {
          delete q[key];
        });
        keyAnswerToRemove.forEach((key) => {
          q.answers.forEach((a) => {
            delete a[key];
          });
        });
      }

      testInfo.questions.sort((a, b) => a.order - b.order);
    }

    const areTestsEqual = computed(() => {
      if (!vacancy.value.hasTest) {
        return false;
      }
      const publishedTestInfoDuplicate = { ...publishedTestInfo.value };
      redactingTestInfo.value = { ...baseTestInfo.value };
      const redactingTestInfooDuplicate = { ...redactingTestInfo.value };

      if (
        Object.keys(publishedTestInfoDuplicate).length === 0
        || Object.keys(redactingTestInfooDuplicate).length === 0
      ) {
        return false;
      }

      removeTestKeysForEqual(publishedTestInfoDuplicate);
      removeTestKeysForEqual(redactingTestInfooDuplicate);

      return isEqual(publishedTestInfoDuplicate, redactingTestInfooDuplicate);
    });

    const areTestsEqualText = computed(() => {
      return areTestsEqual.value ? t('changesSaved') : t('changesNotSaved');
    });

    const {
      deleteQuestion: deleteBaseQuestion,
      createQuestion: createBaseQuestion,
      updateQuestion: updateBaseQuestion,
      uploadQuestionImage: uploadBaseQuestionImage,
      deleteQuestionImage: deleteQuestionImageBase,
    } = useQuestion();
    const { fetchById } = useVacancies();
    const timeout = 1000;

    const testInfo = ref(null);
    const testType = ref(testTypes.NOT_REQUIRED);
    const scoreType = ref(testEstimationTypes.PERCENT);
    const minimalScore = ref(100);
    const hours = ref(null);
    const minutes = ref(null);
    const timer = ref(null);

    const testTimer = ref(null);
    const testLoading = ref(false);

    const questionTimers = ref({});
    const loadingQuestions = ref([]);

    const loading = computed(() => loadingQuestions.value.length > 0 || testLoading.value);

    const vacancyDraft = computed(() => vacancy.value.status === vacancyStatus.DRAFT);
    const vacancyActiveWithPublishedTest = computed(
      () => vacancy.value.hasTest && !vacancyDraft.value,
    );
    const vacancyActiveWithoutTest = computed(
      () => !vacancy.value.hasTest && !vacancy.value.hasRedactingTest && !vacancyDraft.value,
    );
    const vacancyDraftWithPublishedTest = computed(
      () => vacancy.value.hasTest && vacancyDraft.value,
    );
    const vacancyDraftWithRedactingTest = computed(
      () => vacancy.value.hasRedactingTest && vacancyDraft.value,
    );
    const vacancyDraftWithoutTest = computed(
      () => !vacancy.value.hasTest && !vacancy.value.hasRedactingTest && vacancyDraft.value,
    );
    const vacancyWithoutTest = computed(
      () => vacancyActiveWithoutTest.value || vacancyDraftWithoutTest.value,
    );
    const vacancyTestWithType = computed(() => testType.value !== testTypes.NOT_REQUIRED);
    const vacancyActiveWithNotRequiredTest = computed(
      () => !vacancyDraft.value && vacancyTestWithType.value,
    );

    const isBackButtonDisabled = computed(() => {
      return disableBackButton.value
             || (vacancy.value?.isAiInterviewCompiling || vacancy.value?.isAiInterviewRecompiling) && !vacancy.value?.isAiInterviewCompiled;
    });

    const totalMinutes = computed(() => Number(hours.value * 60) + Number(minutes.value));
    const testTimerRadio = ref(false);

    watch(totalMinutes, () => {
      testTimerRadio.value = hours.value !== 0 || minutes.value !== 0;
    });

    watch(testTimerRadio, () => {
      if (!testTimerRadio.value) {
        hours.value = 0;
        minutes.value = 0;
      }
    });

    const successBtnText = computed(() => {
      if (finishButtonText.value) {
        return finishButtonText.value;
      }
      return t('save');
    });

    const showValidationMessagesGlobal = ref(false);

    const defaultAnswer = {
      id: -1,
      isCorrect: true,
      isValid: false,
      answer: null,
    };

    const defaultQuestion = {
      id: 0,
      localId: 0,
      image: null,
      order: 1,
      question: null,
      score: 1,
      testId: null,
      type: testQuestionTypes.SELECT,
      isValid: false,
      answers: [{ ...defaultAnswer }],
    };

    const questions = ref([cloneDeep(defaultQuestion)]);

    const hasInvalidQuestions = computed(() => questions.value.filter((q) => !q.isValid).length > 0);
    const hasNoQuestions = computed(() => questions.value.length === 0);
    const hasNoMinimalScore = computed(() => !minimalScore.value && minimalScore.value !== 0);

    const isValid = computed(
      () => !(hasInvalidQuestions.value || hasNoQuestions.value || hasNoMinimalScore.value),
    );

    const totalPoints = computed(() => questions.value.reduce((sum, q) => sum + Number(q.score), 0));

    const minimalScoreWithPercent = computed(() => (minimalScore.value
      ? Math.ceil(
        (questions.value.reduce((sum, q) => sum + Number(q.score), 0) / 100)
              * minimalScore.value,
      )
      : totalPoints.value));

    const lastQuestionOrder = computed(() => {
      if (questions.value.length === 0) {
        return 0;
      }

      return Math.max(...questions.value.map((q) => q.order));
    });

    async function loadTestDataForEqual() {
      if (vacancy.value.hasTest && !vacancy.value.hasRedactingTest) {
        await getTestInfo(vacancyId.value);
        publishedTestInfo.value = cloneDeep(baseTestInfo.value);
        await createRedactingTestInfo(vacancyId.value, baseTestInfo.value.id);
        await fetchById(vacancyId.value);
        return;
      }
      if (vacancy.value.hasTest && vacancy.value.hasRedactingTest) {
        await getTestInfo(vacancyId.value);
        publishedTestInfo.value = cloneDeep(baseTestInfo.value);
        await getRedactingTestInfo(vacancyId.value);
        return;
      }

      await getRedactingTestInfo(vacancyId.value);
    }

    async function loadTestData() {
      await loadTestDataForEqual();

      if (baseTestInfo.value) {
        testInfo.value = cloneDeep(baseTestInfo.value);
      }

      if (testInfo.value) {
        hours.value = Math.floor(testInfo.value?.timer / 60);
        minutes.value = testInfo.value?.timer % 60;
        if (!testLoading.value) {
          testType.value = vacancyWithoutTest.value ? testTypes.NOT_REQUIRED : testInfo.value.type;
        }
        minimalScore.value = testInfo.value.minimalScore;
      }

      if (testInfo.value?.questions && testInfo.value.questions.length) {
        const baseTimestamp = Date.now();
        const newQuestions = testInfo.value.questions.map((q, index) => {
          const question = cloneDeep(q);
          question.key = question.id ? `q-${question.id}` : `q-temp-${baseTimestamp}-${index}`;
          question.localId = question.localId || question.id;
          if (!question.answers || question.answers.length < 1) {
            question.answers = [{ ...defaultAnswer }];
          }
          return question;
        });

        newQuestions.sort((a, b) => a.order - b.order);
        questions.value = newQuestions;
      } else {
        questions.value = [];
      }
    }

    function addQuestion() {
      const minLocalId = Math.min(...questions.value.filter((q) => q.id < 0).map((q) => q.id));
      const maxOrder = Math.max(...questions.value.map((q) => q.order));
      const maxLocalId = Math.max(...questions.value.filter((q) => q.localId).map((q) => q.localId));
      const question = cloneDeep(defaultQuestion);
      question.id = Number.isFinite(minLocalId - 1) ? minLocalId - 1 : -1;
      question.order = Number.isFinite(maxOrder + 1) ? maxOrder + 1 : 1;
      question.localId = Number.isFinite(maxLocalId + 1) ? maxLocalId + 1 : 1;

      questions.value.push(question);
    }

    async function removeTest() {
      if (vacancyDraftWithPublishedTest.value) {
        await getTestInfo(vacancyId.value);
        await removeBaseTest(vacancyId.value, baseTestInfo.value.id);
      }
      if (vacancyDraftWithRedactingTest.value) {
        await getRedactingTestInfo(vacancyId.value);
        await removeBaseTest(vacancyId.value, baseTestInfo.value.id);
      }
      await deleteAllTests();
    }

    async function publishTest() {
      await publishBaseTest(vacancyId.value, testInfo.value.id);
    }

    async function unpublishTest() {
      await unpublishBaseTest(vacancyId.value);
      emit('finish');
    }

    async function saveTestSettings() {
      testLoading.value = true;

      try {
        if (testType.value === testTypes.NOT_REQUIRED) {
          if (vacancyDraft.value) {
            if (vacancy.value.hasTest || vacancy.value.hasRedactingTest) {
              await removeTest();
            }
          } else {
            if (vacancy.value.hasTest) {
              await unpublishBaseTest(vacancyId.value);
            }

            if (vacancy.value.hasRedactingTest && testInfo.value?.id) {
              await removeBaseTest(vacancyId.value, testInfo.value.id);
            }
          }

          await fetchById(vacancyId.value);
          await deleteAllTests();
          await Question.deleteAll();
          testInfo.value = null;
          questions.value = [];
          testTimerRadio.value = false;
          hours.value = 0;
          minutes.value = 0;
          return;
        }

        if (!vacancy.value.hasTest && !vacancy.value.hasRedactingTest) {
          await createTest(vacancyId.value, {
            type: testType.value,
            scoreType: scoreType.value,
            minimalScore: Number(minimalScore.value),
            timer: totalMinutes.value,
          });
          await fetchById(vacancyId.value);
          await loadTestData();
        } else {
          await updateBaseTest(vacancyId.value, testInfo.value.id, {
            type: testType.value,
            scoreType: scoreType.value,
            minimalScore: Number(minimalScore.value),
            timer: totalMinutes.value,
          });
          await fetchById(vacancyId.value);
          await loadTestData();
        }
      } catch (e) {
        root?.$toast?.error('Ошибка обновления теста');
      } finally {
        testLoading.value = false;
      }
    }

    async function updateTest({ debounce = false } = {}) {
      if (testTimer.value) {
        clearTimeout(testTimer.value);
        testTimer.value = null;
      }

      if (!debounce) {
        await saveTestSettings();
        return;
      }

      testTimer.value = setTimeout(() => {
        saveTestSettings();
      }, timeout);
    }

    async function uploadQuestionImage(questionId, img) {
      return uploadBaseQuestionImage(
        questionId,
        objectToFormData({
          image: img,
        }),
      );
    }

    async function createQuestion(question, img) {
      const response = await createBaseQuestion(vacancyId.value, testInfo.value.id, question);
      const questionId = response.response.data.data.id;

      for (const q of questions.value) {
        if (q.localId === question.localId) {
          q.id = questionId;
          break;
        }
      }

      if (img) {
        const imgResponse = await uploadQuestionImage(questionId, img);
        for (const q of questions.value) {
          if (q.localId === question.localId) {
            q.image = imgResponse.response.data.data.image;
            break;
          }
        }
      }
    }

    async function updateQuestion(question, img) {
      await updateBaseQuestion(vacancyId.value, question.id, question);
      if (img) {
        const imgResponse = await uploadQuestionImage(question.id, img);
        questions.value = questions.value.map((q) => {
          if (q.id === question.id) {
            q.image = imgResponse.response.data.data.image;
            question.image = imgResponse.response.data.data.image;
          }
          return q;
        });
      }
    }

    async function deleteQuestionImage(testQuestionId) {
      await deleteQuestionImageBase(testQuestionId);
      questions.value.map((q) => {
        if (q.id === testQuestionId) {
          q.image = null;
          return q;
        }

        return q;
      });
    }

    const updatingQuestionsIds = ref(new Map());
    const lockedForCreatingQuestionsIds = ref(new Map());

    async function saveQuestion(question, img) {
      if (localLoading.value) return;

      const isEmptyAnswersAndNotFree = question.type !== testQuestionTypes.FREE
        && (!question.question || !question.answers.some((a) => a.answer));
      const isEmptyQuestion = !question.question;

      if (isEmptyAnswersAndNotFree || isEmptyQuestion) return;

      const timerQuestionId = question.localId;

      if (!loadingQuestions.value.includes(timerQuestionId)) {
        loadingQuestions.value.push(timerQuestionId);
      }

      if (questionTimers.value[timerQuestionId]) {
        clearTimeout(questionTimers.value[timerQuestionId]);
        delete questionTimers.value[timerQuestionId];
      }

      questionTimers.value[timerQuestionId] = setTimeout(async () => {
        try {
          const orders = questions.value
            .filter((q) => q.id !== question.id && q.id > 0)
            .map((q) => q.order);

          const maxOrder = orders.length ? Math.max(...orders) : 0;

          if (!question.order || question.order > maxOrder + 1) {
            question = cloneDeep(question);
            question.order = maxOrder + 1;
          }

          if (question.id > 0) {
            await updateQuestion(question, img);
          } else if (!lockedForCreatingQuestionsIds.value.has(timerQuestionId)) {
            lockedForCreatingQuestionsIds.value.set(timerQuestionId, true);

            try {
              await createQuestion(question, img);

              const data = updatingQuestionsIds.value.get(timerQuestionId);
              if (data?.question?.id > 0) {
                await updateQuestion(data.question, data.img);
              }
            } finally {
              lockedForCreatingQuestionsIds.value.delete(timerQuestionId);
            }
          }
        } catch (e) {
          if (!e.code || e.code !== 422) {
            root?.$toast?.error(t('errorSavingTest'));
          }
        } finally {
          updatingQuestionsIds.value.delete(timerQuestionId);
          loadingQuestions.value = loadingQuestions.value.filter((qId) => qId !== timerQuestionId);
        }
      }, timeout);
    }

    async function deleteQuestion(qId) {
      const deletedQuestion = questions.value.find((q) => q.id === qId);

      if (!deletedQuestion) return;

      questions.value = questions.value
        .filter((q) => q.id !== qId)
        .map((q) => {
          if (q.order <= deletedQuestion.order) {
            return q;
          }

          q.order -= 1;

          return q;
        });

      if (qId > 0) {
        try {
          await deleteBaseQuestion(vacancyId.value, qId);
          await loadTestData();
        } catch (e) {
          await loadTestData();
          throw e;
        }
      }
    }

    async function changeTestType() {
      switch (testType.value) {
        case testTypes.NOT_REQUIRED: {
          await updateTest();
          break;
        }
        case testTypes.CUTOFF: {
          await updateTest();
          break;
        }
        case testTypes.EXPLANATION: {
          await updateTest();
          break;
        }
        default: {
          break;
        }
      }
    }

    function changeQuestionOrderDown(question) {
      let canMove = true;
      let save = true;

      questions.value.forEach((q) => {
        if (q.order === question.order + 1 && canMove) {
          q.order -= 1;
          save = q.id > 0;
          question.order += 1;
          canMove = false;
          q.key = Date.now();
          question.key = Date.now();
        }
      });

      if (!save) {
        return;
      }

      saveQuestion(question);
    }

    function changeQuestionOrderUp(question) {
      let canMove = true;
      let save = true;
      questions.value.forEach((q) => {
        if (q.order === question.order - 1 && canMove) {
          q.order += 1;
          save = q.id > 0;
          question.order -= 1;
          canMove = false;
          q.key = Date.now();
          question.key = Date.now();
        }
      });

      if (!save) {
        return;
      }

      saveQuestion(question);
    }

    const minimalScoreErrorMessages = computed(() => {
      const errors = [];

      if (hasNoMinimalScore.value) {
        errors.push(t('requiredField'));
      }

      return errors;
    });

    async function publish() {
      if (testType.value !== testTypes.NOT_REQUIRED && !isValid.value) {
        showValidationMessagesGlobal.value = true;
        await nextTick();
        showValidationMessagesGlobal.value = false;
        return;
      }

      testLoading.value = true;

      try {
        if (testType.value === testTypes.NOT_REQUIRED) {
          if (vacancyActiveWithPublishedTest.value) {
            await unpublishBaseTest(vacancyId.value);
          } else if (vacancyDraft.value) {
            await removeTest();
          }
          emit('finish');
          return;
        }

        await publishTest();
        await deleteAllTests();
        await Question.deleteAll();
        emit('finish');
      } catch (e) {
        root?.$toast?.error(t('errorPublishingTest'));
      } finally {
        testLoading.value = false;
      }
    }

    function goBack() {
      emit('goBack');
    }

    function getQuestionKey(question) {
      return question.id || question.localId || question.key;
    }

    onMounted(async () => {
      localLoading.value = true;
      await loadTestData();
      localLoading.value = false;
    });

    onBeforeUnmount(() => {
      if (testTimer.value) clearTimeout(testTimer.value);
      Object.values(questionTimers.value).forEach(clearTimeout);
    });

    return {
      questions,
      addQuestion,
      testType,
      minimalScore,
      minimalScoreWithPercent,
      hours,
      minutes,
      scoreType,
      saveQuestion,
      publishTest,
      vacancy,
      testInfo,
      updateTest,
      totalPoints,
      deleteQuestion,
      successBtnText,
      uploadQuestionImage,
      changeTestType,
      changeQuestionOrderDown,
      changeQuestionOrderUp,
      publish,
      loading,
      testTypes,
      showValidationMessagesGlobal,
      deleteQuestionImage,
      lastQuestionOrder,
      localLoading,
      testLoading,
      goBack,
      minimalScoreErrorMessages,
      vacancyStatus,
      unpublishTest,
      vacancyActiveWithPublishedTest,
      testTimerRadio,
      totalMinutes,
      areTestsEqual,
      getQuestionKey,
      areTestsEqualText,
      vacancyDraft,
      vacancyActiveWithNotRequiredTest,
      vacancyTestWithType,
      isBackButtonDisabled,
    };
  },
};
</script>

<style scoped lang="scss">
.vacancy-step-testing {
  &__title {
    color: #000000;
    font-size: 18px;
    line-height: 28px;
    max-width: 165px;
    @media screen and (max-width: 996px) {
      margin-bottom: 16px;
    }
  }
  &__choice-type {
    max-width: 70%;
    @media screen and (max-width: 1240px) {
      max-width: 85%;
    }
    @media screen and (max-width: 996px) {
      flex-direction: column;
      max-width: 100%;
    }
  }
  &__choice-score-type {
    @media screen and (max-width: 996px) {
      flex-direction: column;
    }
    &_radio-group {
      width: 70% !important;
      @media screen and (max-width: 996px) {
        width: 100% !important;
      }
    }
  }
  &__q {
    width: 100%;
    margin-top: 20px;
  }
  &__radio-label {
    max-width: 75%;
    margin-left: 33px;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.25px;
    color: #333333;
    margin-top: 4px;
  }
  &__radio-input-block {
    margin: 10px 0 0 33px;
    display: flex;
    &_points {
      display: flex;
      span {
        margin: 7px 20px 0 5px;
      }
    }
    &_label {
      font-size: 14px;
      margin-top: 7px;
      @media screen and (max-width: 370px) {
        font-size: 16px;
      }
      span {
        @media screen and (max-width: 370px) {
          display: none;
        }
      }
    }
  }
  &__radio-input {
    max-width: 70px;
  }
  &__radio-input .v-input__control {
    display: none !important;
  }
  &__radio-warning {
    font-weight: 700;
    max-width: 75%;
    font-size: 14px;
    margin: 0px 0 12px 33px;
  }
  &__points-info {
    @media screen and (max-width: 768px) {
      flex-direction: column;
      margin-top: 0;
    }
    &_radio-group {
      width: 70%;
    }
    &_title {
      color: #000000;
      font-size: 18px;
      line-height: 28px;
      @media screen and (max-width: 996px) {
        margin-bottom: 16px;
      }
    }
    &_time {
      margin: 0 15px;
      max-width: 80px;
    }
    &_inputs {
      max-width: 80px;
      font-size: 16px;
      line-height: 16px;
      div {
        display: flex;
        align-items: center;
        font-size: 14px;
      }
    }
    &_inputs div {
      > input:nth-child(1) {
        margin: 0 15px 0 0;
      }
    }
  }
  &__buttons-controls {
    position: sticky;
    border-top: 1px solid rgba(196, 196, 196, 0.5);
    margin-left: -2px;
    bottom: 0;
    background: #fff;
    width: 101%;
    padding: 20px 0;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-start;
    &_changes-text {
      display: flex;
      align-items: center;
      img {
        height: 16px;
        width: 16px;
        margin-right: 8px;
      }
      span {
        font-size: 16px;
        line-height: 16px;
        font-weight: 400;
      }
    }
  }
}

.questions-block {
  max-width: 75%;
  padding-bottom: 20px !important;
  @media screen and (max-width: 1300px) {
    max-width: 85%;
  }
  @media screen and (max-width: 864px) {
    max-width: 100%;
  }
}

.add-question-block {
  margin-top: 10px;
  margin-left: -13px;
}

input {
  border: 1px solid #c2c2c2;
  border-radius: 4px;
  padding: 6px 12px;
  background-color: #fff;
  outline: none;
}

.required-field {
  &::after {
    content: '*';
    color: red;
    font-size: 12px;
    margin-left: 2px;
  }
}

input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
}

input[type='number'],
input[type='number']:hover,
input[type='number']:focus {
  appearance: none;
  -moz-appearance: textfield;
}

.v-input--radio-group--column .v-radio:not(:last-child):not(:only-child) {
  margin-bottom: 0;
}

.v-text-field::v-deep {
  .v-input__control {
    .v-text-field__details {
      min-width: 240px !important;
      padding: 0;
      margin: 5px 0;
    }
  }
}

::v-deep input::-webkit-outer-spin-button,
::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.v-radio::v-deep {
  max-width: fit-content;
  .v-label {
    color: #333333 !important;
  }
}

.v-input--checkbox::v-deep {
  .v-input__control {
    .v-input__slot {
      margin-bottom: 0;
    }
    .v-messages {
      display: none;
    }
  }
}

.h-100 {
  height: 100%;
}
.black-text {
  color: #000 !important;
}
.green-text {
  color: #1fc57f;
}
</style>

<i18n locale="en" lang="yaml">
vacancy: Job
step: Choose an interview template
next: Next
goBack: Go back
testing: Testing
explanationTestTextLabel: 'Conducted prior to the the online interview to weed out unsuitable candidates'
explanationTestTextWarning: 'The candidate cannot move on the the video interview, if they have even one incorrect answer'
cutoffTestTextLabel: "Conducted prior to the video interview to detail the candidate's skills"
passingScore: Passing score
passingPercentage: Passing percentage
timeToComplete: Time to complete
hours: hours
minutes: minutes
seconds: seconds
addQuestion: Add a question
cancel: Cancel
publishAVacancy: Publish a job?
explanationTestTextSubtitle: Specify the passing percentage at which the candidate will move on to the video interview
notRequired: Not required
cutoff: Cut-off line
clarifying: Clarification
numberOfPoints: Number of points
requiredField: This field is required
selectError: "A question with a choice of 1 answer should have 2 or more answer options!"
explanationTestTextWarning0: "All candidates will have access to a video interview"
save: Save
publish: Publish
unpublish: Unpublish test
withoutLimit: Without limit
withLimit: With limit
changesSaved: The test is saved
changesNotSaved: The changes are not saved
errorPublishTest: Error publishing test
errorSavingTest: Error saving question
</i18n>

<i18n locale="ru" lang="yaml">
vacancy: Вакансия
step: Тестирование кандидата
next: Далее
goBack: Назад
testing: Тестирование
explanationTestTextLabel: 'Проводится перед видео-собеседованием, чтобы отсечь неподходящих кандидатов'
explanationTestTextWarning: 'Кандидат не сможет пройти видео-собеседование, если даст хоть один неверный ответ'
cutoffTestTextLabel: 'Проводится до видео-собеседования, чтобы детализировать навыки кандидата'
passingScore: Проходной балл
passingPercentage: Проходной процент
timeToComplete: Время на прохождение
hours: часов
minutes: минут
seconds: секунд
addQuestion: Добавить вопрос
cancel: Отмена
publishAVacancy: Опубликовать вакансию
explanationTestTextSubtitle: Укажите проходной процент, при котором кандидат будет допущен к видео-собеседованию
notRequired: Не требуется
cutoff: Отсекающее
clarifying: Уточняющее
numberOfPoints: Количество баллов
requiredField: Обязательное поле для заполнения
selectError: "У вопроса с выбором 1 ответа должно быть быть 2 и более вариантов ответа!"
explanationTestTextWarning0: "Все кандидаты получат доступ к видео-собеседованию"
save: Сохранить
publish: Опубликовать
unpublish: Снять тест с публикации
withoutLimit: Без ограничения
withLimit: С ограничением
changesSaved: Тест сохранен
changesNotSaved: Изменения не сохранены
errorPublishingTest: Ошибка при публикации теста
errorSavingTest: Ошибка сохранения вопроса
</i18n>
